import React, { useEffect, useMemo } from 'react'
import { Grid, InputLabel, MenuItem, TextField } from '@material-ui/core'
import ActionModal from 'asterX-UI/Modals/ActionModal'
import { SelectPlaceholder } from 'asterX-UI/Inputs'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { useSnackbar } from 'notistack'
import { addMonths, format } from 'date-fns'
import { ru } from 'date-fns/locale'
import { useContractStore } from 'context/ContractStore'
import { useApplicationStore } from 'context/ApplicationStore'
import { useStyles } from '../../pages/ContractPage/components/AddEvents/add-events.styles'
import SuspensionSection from './components/SuspensionSection'
import WriteOffSection from './components/WriteOffSection'
import BuybackSection from './components/BuybackSection'

const DEFERRAL_TYPES = [
  { label: 'Write-OFF', value: 'WRITE_OFF' },
  { label: 'Buyback', value: 'BUYBACK' },
  { label: 'Suspension', value: 'SUSPENSION' }
]

const CLIENT_TYPES = [
  { label: 'ТОО', value: 'TOO' },
  { label: 'ИП', value: 'IE' },
  { label: 'Физ. лицо', value: 'INDIVIDUAL' }
]

const SUSPENSION_REASONS = [
  'АФР список (мошенники)',
  'Уходит в армию',
  'Болеет сам или близкий родственник',
  'Банкротство',
  'Потерял место работы',
  'Временное банкротство (затруднения)',
  'Семейные причины',
  'Умершие',
  'Другие'
]

const BUYBACK_REASONS = ['Добровольный выкуп', 'Техническое состояние авто', 'Финансовые причины клиента', 'Другие']

const WRITE_OFF_REASONS = ['Мошенник', 'Умершие', 'Тюрма', 'Безнадежный клиент', 'Потеря залогового имущества']
const ExpandIcon = props => <ExpandMoreIcon {...props} style={{ margin: '0 8px' }} />

const PATHS = {
  suspensionApplication: 'suspension.files.application',
  suspensionWorkCert: 'suspension.files.workCertificate',
  suspensionIncomeProof: 'suspension.files.incomeProof',
  buybackInspection: 'buyback.files.inspectionReport',
  buybackConsent: 'buyback.files.buybackConsent'
}

const monthPillStyle = { border: '1px solid rgba(0,0,0,0.12)', borderRadius: 8 }

const AddDeferralModal = ({
  open,
  onClose,
  onCancel,
  mode = 'create',
  value,
  onPathChange,
  onDateChange,
  onFileChange,
  onDeleteFile,
  onValidate,
  onSubmit
}) => {
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()

  const {
    contractStore: { contractAll },
    contractStoreFunc
  } = useContractStore()

  const {
    applicationStore: { resubmitErrors, deferral },
    applicationStoreFunc: {
      handleDeferralChangeByPath,
      handleDeferralDateChange,
      clearDeferral,
      submitSuspensionApplication,
      submitBuybackApplication,
      submitWriteOffApplication,

      validateResubmitFields,
      clearResubmitError,
      clearAllResubmitErrors
    }
  } = useApplicationStore()

  const isExternal = mode === 'resubmit'
  const form = isExternal ? value : deferral
  const formErrors = resubmitErrors || {}
  const {
    deferralType,
    suspension = { files: {} },
    writeOff = {},
    buyback = { files: {} },
    commonReason = ''
  } = form || {}

  useEffect(() => {
    if (!open) return
    if (contractAll?.id) contractStoreFunc.getPhoneNumbers()
    clearDeferral()
    clearAllResubmitErrors()
  }, [open, contractAll?.id])

  const monthsPreview = useMemo(() => {
    const start =
      suspension?.startDate instanceof Date
        ? suspension.startDate
        : suspension?.startDate
        ? new Date(suspension.startDate)
        : null
    if (deferralType !== 'SUSPENSION' || !start || suspension?.termMonths == null) return []
    return Array.from({ length: Number(suspension.termMonths || 0) }).map((_, i) =>
      format(addMonths(start, i), 'LLLL yyyy', { locale: ru })
    )
  }, [deferralType, suspension?.startDate, suspension?.termMonths])

  const handlePathChangeLocal = e => {
    if (e?.target?.name) clearResubmitError(e.target.name)
    if (isExternal) onPathChange?.(e)
    else handleDeferralChangeByPath(e.target.name, e.target.value)
  }

  const handleReasonChange = e => {
    handlePathChangeLocal({ target: { name: 'commonReason', value: e.target.value } })
  }

  const handleFileChangeLocal = (path, f) => {
    if (path) clearResubmitError(path)
    if (isExternal) onFileChange?.(path, f)
    else handleDeferralChangeByPath(path, f)
  }

  const handleDeleteFileLocal = path => {
    if (path) clearResubmitError(path)
    if (isExternal) onDeleteFile?.(path)
    else handleDeferralChangeByPath(path, null)
  }

  const handleDateChangeLocal = (path, date) => {
    if (path) clearResubmitError(path)
    if (isExternal) onDateChange?.(path, date)
    else handleDeferralDateChange(path, date)
  }

  const isRequired = v => !!v
  const isDate = v => v instanceof Date || (!!v && !isNaN(new Date(v)))
  const startOfToday = () => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d
  }
  const isFutureOrTodayDate = v => {
    if (!v) return false
    const d = v instanceof Date ? new Date(v) : new Date(v)
    if (isNaN(d)) return false
    d.setHours(0, 0, 0, 0)
    return d.getTime() >= startOfToday().getTime()
  }

  const hasFile = f => !!(f && (f.base64 || f.raw || f.file))

  const suspensionRulesCreate = [
    { name: 'deferralType', validate: v => v === 'SUSPENSION', error: 'Тип отсрочки' },
    { name: 'suspension.termMonths', validate: isRequired, error: 'Срок отсрочки' },
    { name: 'suspension.startDate', validate: v => isDate(v), error: 'Дата начала' },
    { name: 'suspension.startDate', validate: v => !!v, error:null },
    { name: 'suspension.startDate', validate: v => !v || isDate(v), error: 'Некорректная дата' },
    {
      name: 'suspension.startDate',
      validate: v => !v || isFutureOrTodayDate(v),
      error: 'Дата не может быть в прошлом'
    },
    { name: 'suspension.reason', validate: isRequired, error: 'Причина отсрочки' },
    { name: 'suspension.files.application', validate: hasFile, error: 'Заявление на отсрочку' },
    { name: 'suspension.files.workCertificate', validate: hasFile, error: 'Справка с места работы' },
    { name: 'suspension.files.incomeProof', validate: hasFile, error: 'Подтверждение потери дохода' }
  ]

  const writeoffRules = [
    { name: 'deferralType', validate: v => v === 'WRITE_OFF', error: 'Тип отсрочки' },
    { name: 'writeOff.reason', validate: isRequired, error: 'Причина списания' }
  ]

  const buybackRules = [
    { name: 'deferralType', validate: v => v === 'BUYBACK', error: 'Тип отсрочки' },
    { name: 'buyback.car.brand', validate: isRequired, error: 'Марка' },
    { name: 'buyback.car.model', validate: isRequired, error: 'Модель' },
    { name: 'buyback.car.plate', validate: isRequired, error: 'Госномер' },
    { name: 'buyback.car.vin', validate: isRequired, error: 'VIN' },
    { name: 'buyback.reason', validate: isRequired, error: 'Причина выкупа' },
    { name: 'buyback.files.inspectionReport', validate: hasFile, error: 'Акт осмотра обязателен' },
    { name: 'buyback.files.buybackConsent', validate: hasFile, error: 'Согласие на выкуп обязательно' }
  ]

  const commonRules =[
    { name: 'deferralType', validate: isRequired, error: 'Тип отсрочки обязателен' },
    mode === 'create' ? { name: 'commonReason', validate: isRequired, error: 'Причина отсрочки обязательна' } : []]

  const buildRules = current => {
    let base =
      deferralType === 'SUSPENSION'
        ? suspensionRulesCreate
        : deferralType === 'WRITE_OFF'
        ? writeoffRules
        : deferralType === 'BUYBACK'
        ? buybackRules
        : []
    return [...commonRules, ...base]
  }

  const handleConfirm = async () => {
    const rules = buildRules(form)
    clearAllResubmitErrors()
    const ok = onValidate ? onValidate(form) : validateResubmitFields(rules, form)

    if (!ok) {
      enqueueSnackbar('Заполните все обязательные поля', { variant: 'error' })
      return
    }

    if (isExternal) {
      await onSubmit?.(form)
      return
    }

    if (deferralType === 'SUSPENSION') {
      await submitSuspensionApplication(contractAll?.id, onClose)
      return
    }
    if (deferralType === 'BUYBACK') {
      await submitBuybackApplication(contractAll?.id, onClose)
      return
    }
    if (deferralType === 'WRITE_OFF') {
      await submitWriteOffApplication(contractAll?.id, onClose)
      return
    }
    enqueueSnackbar('Неизвестный тип заявки', { variant: 'error' })
  }

  return (
    <ActionModal
      open={open}
      onClose={onClose}
      onCancel={onCancel}
      title={isExternal ? 'Переотправить заявление' : 'Добавить отсрочку'}
      labelConfirm={isExternal ? 'Переотправить' : 'Создать'}
      onConfirm={handleConfirm}
    >
      <Grid item container xs={12} className={classes.eventsContainer}>
        <Grid item xs={12} className={classes.item}>
          <InputLabel shrink className={classes.inputLabel}>
            Тип *
          </InputLabel>
          <SelectPlaceholder
            IconComponent={ExpandIcon}
            name="deferralType"
            value={deferralType}
            onChange={handlePathChangeLocal}
            fullWidth
            placeholder="Не выбрано"
            error={Boolean(formErrors['deferralType'])}
          >
            <MenuItem value={''}>Не выбрано</MenuItem>
            {DEFERRAL_TYPES.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </SelectPlaceholder>
        </Grid>

        {mode === 'create' && (
          <Grid item xs={12} className={classes.item}>
            <InputLabel shrink className={classes.inputLabel}>
              Причина отсрочки *
            </InputLabel>
            <TextField
              name="commonReason"
              value={commonReason || ''}
              onChange={handleReasonChange}
              placeholder="Укажите причину отсрочки"
              fullWidth
              multiline
              minRows={2}
              variant="outlined"
              error={Boolean(formErrors['commonReason'])}
            />
          </Grid>
        )}

        {deferralType === 'WRITE_OFF' && (
          <WriteOffSection
            writeOff={writeOff}
            onPathChange={handlePathChangeLocal}
            IconComponent={ExpandIcon}
            clientTypes={CLIENT_TYPES}
            reasons={WRITE_OFF_REASONS}
            errors={formErrors}
          />
        )}

        {deferralType === 'BUYBACK' && (
          <BuybackSection
            buyback={buyback}
            onPathChange={handlePathChangeLocal}
            onFileChange={handleFileChangeLocal}
            onDeleteFile={handleDeleteFileLocal}
            IconComponent={ExpandIcon}
            clientTypes={CLIENT_TYPES}
            reasons={BUYBACK_REASONS}
            inspectionPath={PATHS.buybackInspection}
            consentPath={PATHS.buybackConsent}
            errors={formErrors}
          />
        )}

        {deferralType === 'SUSPENSION' && (
          <SuspensionSection
            suspension={suspension}
            monthsPreview={monthsPreview}
            onPathChange={handlePathChangeLocal}
            onDateChange={handleDateChangeLocal}
            onFileChange={handleFileChangeLocal}
            onDeleteFile={handleDeleteFileLocal}
            IconComponent={ExpandIcon}
            clientTypes={CLIENT_TYPES}
            reasons={SUSPENSION_REASONS}
            applicationPath={PATHS.suspensionApplication}
            workCertPath={PATHS.suspensionWorkCert}
            incomeProofPath={PATHS.suspensionIncomeProof}
            monthPillStyle={monthPillStyle}
            errors={formErrors}
          />
        )}
      </Grid>
    </ActionModal>
  )
}

export default AddDeferralModal
